<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-0">
        <v-col cols="2">
          <v-menu transition="scale-transition" offset-y v-model="dateMenu">
            <template v-slot:activator="{ on }">
              <v-text-field label="Month" hide-details dense readonly v-on="on" v-model="monthAsDate" outlined append-icon="mdi-menu-down" @click:append="dateMenu=true"/>
            </template>
            <v-date-picker v-model="month" type="month" no-title @change="refresh"/>
          </v-menu>
        </v-col>
        <v-col cols="2">
          <v-select :items="licenses" label="License" v-model="license" @change="refresh" dense hide-details outlined class="ml-6"/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Warehouse return details', buildReport())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 180px)">
      <span v-html="buildReport()"/>
    </v-card>
  </div>
</template>

<style>
.report table {
  padding-right:14px;
}
.report tbody tr td{
  border:none !important;
  height:24px !important;
}
.report tbody tr:hover {
  background-color: #fff !important;
}
</style>

<script>
import {format,add,lastDayOfMonth} from 'date-fns'
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'

export default {
  name: 'ReportsExciseWarehouseDetails',
  mixins: [printHtml],
  data() {
    return {
      dateMenu: false,
      month: '',
      license: '',
      loading: true,
      report: {laa:[],litres:[],lines:[]},
      transactions: []
    }
  },
  computed: {
    monthAsDate() {
      return this.$formatDate(this.month+'-01', 'MMMM, yyyy');
    },
    licenses() {
      return [...new Set(this.$store.getters.sites(true, {B262:true}).map(site => site.warehouseLicense))];
    },
    company() {
      return this.$store.getters.company;
    }
  },
  async created() {
    this.$emit('name', 'Warehouse Licensee return details')
    this.month = format(add(new Date(), {months:-1}), 'yyyy-MM');
    this.license = this.licenses[0];
    this.refresh();
  },
  methods: {
    async refresh() {
      this.loading = true;
      let to = format(lastDayOfMonth(add(new Date(this.month+'-01'),{days:1})), 'yyyy-MM-dd');
      const results = await this.$apiCall(`query ReportsExciseWarehouseDetails($license:String!,$date:Date!,$to:Date!) {
                                              exciseWarehouseReport(license:$license,date:$date) {
                                                laa {openingInventory,totalAdditions,totalNonDutyReductions,totalDutyReductions,totalReductions,closingInventory},
                                                litres {openingInventory,totalAdditions,totalNonDutyReductions,totalDutyReductions,totalReductions,closingInventory},
                                                lines{excise,laa,litres},
                                              }
                                              productsActivity(from:$date,to:$to,license:$license,inBond:true) {
                                                date,description,bottles,cases,litres,laa,productName,excise,strength,comments
                                              }
                                            }`,
                                          {license:this.license,date:this.month+'-01',to});
      if (!results.error) {
        this.report = results.data.exciseWarehouseReport;
        this.transactions = results.data.productsActivity;
      }
      this.loading = false;
    },

    buildReport() {
      const lines = [`<style>
                      @page {size:landscape}
                      .report-excise-warehouse-details {font-family: Roboto, sans-serif; font-size:12px;width:100%;border-spacing:0}
                      .report-excise-warehouse-details body {font-family: Roboto, sans-serif; font-size:14px}
                      .report-excise-warehouse-details th,td {height:36px;padding:0 16px;vertical-align:middle}
                      .report-excise-warehouse-details .title {font-size:18px;text-transform:uppercase}
                      .report-excise-warehouse-details .sup-title {font-weight:normal;font-size:22px}
                      .report-excise-warehouse-details .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                      .report-excise-warehouse-details .header th {border-bottom:1px solid #000;margin-bottom:20px}
                      .report-excise-warehouse-details .text-left {text-align:left}
                      .report-excise-warehouse-details .text-right {text-align:right}
                      .report-excise-warehouse-details .font-weight-bold {font-weight:bold}
                      .report-excise-warehouse-details .total-row {font-weight:bold}
                    </style>
                    <table class="report-excise-warehouse-details">
                      <thead>
                        <tr>
                          <th colspan="8" class="sup-title">${this.$store.getters.company.name}</th>
                        </tr>
                        <tr><th colspan="8" class="title">Excise warehouse details</th></tr>
                        <tr><th colspan="8" class="sub-title">${this.license} ${this.monthAsDate}</th></tr>
                        <tr class="header">
                            <th width="4%" class="text-left"></th>
                            <th width="14%" class="text-left">Date</th>
                            <th width="25%" class="text-left">Product</th>
                            <th width="25%" class="text-left">Description</th>
                            <th width="8%" class="text-right">LAA</th>
                            <th width="8%" class="text-right">Litres</th>
                            <th width="8%" class="text-right">Total LAA</th>
                            <th width="8%" class="text-right">Total Litres</th>
                          </tr>
                       </thead>
                       <tbody>`
      ];
      lines.push(`<tr class="font-weight-bold"><td colspan="6" class="text-right">Opening inventory</td><td class="text-right">${this.number(this.report.laa.openingInventory,3)}</td><td class="text-right">${this.number(this.report.litres.openingInventory,3)}</td>`);
      lines.push(`<tr class="font-weight-bold"><td colspan="8">Additions to inventory</td>`);
      lines.push(...this.details('ProductAdditionDomestic','Domestic non-duty-paid packaged'));
      lines.push(...this.details('ProductAdditionImported','Imported non-duty-paid packaged'));
      lines.push(...this.details('ProductAdditionReturnedDutyPaid','Returned from Duty-paid sources'));
      lines.push(...this.details('ProductAdditionReturnedNonDutyPaid','Returned from Non-duty-paid sources'));
      lines.push(`<tr class="font-weight-bold"><td colspan="6" class="text-right">Total additions</td><td class="text-right">${this.number(this.report.laa.totalAdditions,3)}</td><td class="text-right">${this.number(this.report.litres.totalAdditions,3)}</td>`);

      lines.push(`<tr class="font-weight-bold"><td colspan="8">Reductions to inventory: non-duty-paid</td>`);
      lines.push(...this.details('ProductReductionToRegisteredUsers','Removed for delivery To registered users'));
      lines.push(...this.details('ProductReductionToAccreditedReps','Removed for delivery To accredited representatives'));
      lines.push(...this.details('ProductReductionToDutyFree','Removed for delivery To duty free shops'));
      lines.push(...this.details('ProductReductionToShipsStores','Removed for delivery As ships\' stores'));
      lines.push(...this.details('ProductReductionToLicensedUserDomestic','Removed for delivery To licensed users Domestic'));
      lines.push(...this.details('ProductReductionToLicensedUserImported','Removed for delivery To licensed users Imported'));
      lines.push(...this.details('ProductReductionToOtherExciseWarehouse','Removed for delivery To other excise warehouses'));
      lines.push(...this.details('ProductReductionExported','Exported spirits'));
      lines.push(...this.details('ProductReductionReturnToSpiritsLicensee','Returned to Spirits Licensee'));
      lines.push(...this.details('ProductReductionBreakage','Breakage'));
      lines.push(...this.details('ProductReductionForOtherPurpose','Spirits removed for other purposes'));
      lines.push(`<tr class="font-weight-bold"><td colspan="6" class="text-right">Total non-duty-paid reductions</td><td class="text-right">${this.number(this.report.laa.totalNonDutyReductions,3)}</td><td class="text-right">${this.number(this.report.litres.totalNonDutyReductions,3)}</td>`);

      lines.push(`<tr class="font-weight-bold"><td colspan="8">Reductions to inventory: duty-paid</td>`);
      lines.push(...this.details('ProductReductionDutyPaidPackaged','Packaged (excluding marked special containers)'));
      lines.push(...this.details('ProductReductionDutyPaidMSC','Packaged in marked special containers'));
      lines.push(...this.details('ProductReductionDutyPaidOtherPurposes','Spirits removed for other purposes'));
      lines.push(`<tr class="font-weight-bold"><td colspan="6" class="text-right">Total duty-paid reductions</td><td class="text-right">${this.number(this.report.laa.totalDutyReductions,3)}</td><td class="text-right">${this.number(this.report.litres.totalDutyReductions,3)}</td>`);

      lines.push(`<tr class="font-weight-bold"><td colspan="6" class="text-right">Total reductions</td><td class="text-right">${this.number(this.report.laa.totalReductions,3)}</td><td class="text-right">${this.number(this.report.litres.totalReductions,3)}</td>`);
      lines.push(...this.details('ProductInventoryAdjustment','Inventory adjustments (+ or -)'));
      lines.push(`<tr class="font-weight-bold"><td colspan="6" class="text-right">Closing inventory</td><td class="text-right">${this.number(this.report.laa.closingInventory,3)}</td><td class="text-right">${this.number(this.report.litres.closingInventory,3)}</td>`);

      lines.push(`</tbody>`);
      lines.push(`</table>`);
      return lines.join('');
    },
    number(value, precision=0) {
      return this.$number(value, precision);
    },
    line(excise,laa,noFormat=false) {
      const entry = this.report.lines.find(line => line.excise === excise);
      return entry ? (noFormat ? (laa?entry.laa:entry.litres) : this.number(laa?entry.laa:entry.litres, 3)) : '0.000';
    },
    details(excise, description) {
      const negative = excise.startsWith('ProductReduction') ? -1 : 1;

      const lines = [];
      lines.push(`<tr><td colspan="6">${description}</td><td class="text-right">${this.line(excise,true)}</td><td class="text-right">${this.line(excise,false)}</td>`);
      for (const transaction of this.transactions)
        if (transaction.excise === excise)
          lines.push(`<tr>
                        <td/>
                        <td>${this.$formatDate(transaction.date)}</td>
                        <td>${transaction.productName}</td>
                        <td>${transaction.description}${transaction.comments?`<div><em>${transaction.comments}</em></div>`:''}</td>
                        <td class="text-right">${transaction.strength > 7 ? this.number(transaction.laa*negative,3) : ''}</td>
                        <td class="text-right">${transaction.strength <= 7 ? this.number(transaction.litres*negative,3) : ''}</td>
                        <td/>
                        <td/>
                      </tr>`);
      return lines;
    },

    exportCSV() {
      const lines = [ `"${this.$store.getters.company.name}",,,,,,,`,
                      `"Excise warehouse details",,,,,,,`,
                      `"${this.license} ${this.monthAsDate}",,,,,,,`,
                      `,"Date","Product","Description","LAA","Litres","Total LAA","Total Litres"`,
                      `,,,,,"Opening inventory",${this.report.laa.openingInventory},${this.report.litres.openingInventory}`,
                      `"Additions to inventory",,,,,,,`,
                      `${this.exportDetailsCSV('ProductAdditionDomestic','Domestic non-duty-paid packaged')}`,
                      `${this.exportDetailsCSV('ProductAdditionImported','Imported non-duty-paid packaged')}`,
                      `${this.exportDetailsCSV('ProductAdditionReturnedDutyPaid','Returned from Duty-paid sources')}`,
                      `${this.exportDetailsCSV('ProductAdditionReturnedNonDutyPaid','Returned from Non-duty-paid sources')}`,
                      `,,,,,"Total additions",${this.report.laa.totalAdditions},${this.report.litres.totalAdditions}`,
                      `"Reductions to inventory: non-duty-paid",,,,,,,`,
                      `${this.exportDetailsCSV('ProductReductionToRegisteredUsers','Removed for delivery To registered users')}`,
                      `${this.exportDetailsCSV('ProductReductionToAccreditedReps','Removed for delivery To accredited representatives')}`,
                      `${this.exportDetailsCSV('ProductReductionToDutyFree','Removed for delivery To duty free shops')}`,
                      `${this.exportDetailsCSV('ProductReductionToShipsStores','Removed for delivery As ships\' stores')}`,
                      `${this.exportDetailsCSV('ProductReductionToLicensedUserDomestic','Removed for delivery To licensed users Domestic')}`,
                      `${this.exportDetailsCSV('ProductReductionToLicensedUserImported','Removed for delivery To licensed users Imported')}`,
                      `${this.exportDetailsCSV('ProductReductionToOtherExciseWarehouse','Removed for delivery To other excise warehouses')}`,
                      `${this.exportDetailsCSV('ProductReductionExported','Exported spirits')}`,
                      `${this.exportDetailsCSV('ProductReductionReturnToSpiritsLicensee','Returned to Spirits Licensee')}`,
                      `${this.exportDetailsCSV('ProductReductionBreakage','Breakage')}`,
                      `${this.exportDetailsCSV('ProductReductionForOtherPurpose','Spirits removed for other purposes')}`,
                      `,,,,,"Total non-duty-paid reductions",${this.report.laa.totalNonDutyReductions},${this.report.litres.totalNonDutyReductions}`,
                      `"Reductions to inventory: duty-paid",,,,,,,`,
                      `${this.exportDetailsCSV('ProductReductionDutyPaidPackaged','Packaged (excluding marked special containers)')}`,
                      `${this.exportDetailsCSV('ProductReductionDutyPaidMSC','Packaged in marked special containers')}`,
                      `${this.exportDetailsCSV('ProductReductionDutyPaidOtherPurposes','Spirits removed for other purposes')}`,
                      `,,,,,"Total duty-paid reductions",${this.report.laa.totalDutyReductions},${this.report.litres.totalDutyReductions}`,
                      `,,,,,"Total reductions",${this.report.laa.totalReductions},${this.report.litres.totalReductions}`,
                      `${this.exportDetailsCSV('ProductInventoryAdjustment','Inventory adjustments (+ or -)')}`,
                      `,,,,,"Closing inventory",${this.report.laa.closingInventory},${this.report.litres.closingInventory}`
                    ];

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Excise warehouse details.csv');
    },
    exportDetailsCSV(excise, description) {
      const negative = excise.startsWith('ProductReduction') ? -1 : 1;

      const lines = [];
      lines.push(`"${description}",,,,,,${this.line(excise,true,true)},${this.line(excise,false,true)}`);
      for (const transaction of this.transactions)
        if (transaction.excise === excise)
          lines.push(`,"${this.$formatDate(transaction.date)}","${transaction.productName}","${transaction.description}",${transaction.strength > 7 ? transaction.laa*negative : ''},${transaction.strength <= 7 ? transaction.litres*negative : ''},,`);

      return lines.join('\r\n');
    }
 }
}
</script>